/**
 * useBootState Hook
 *
 * React hook for accessing the boot coordinator state.
 * Used by App.tsx and BootScreen to respond to boot phases.
 */

import { useState, useEffect, useCallback } from 'react';
import { bootCoordinator, BootState, BootPhase } from '../services/bootCoordinator';

export interface UseBootStateReturn {
  /** Current boot state */
  bootState: BootState;
  /** Current boot phase */
  phase: BootPhase;
  /** Whether boot is complete and app can render */
  isReady: boolean;
  /** Whether boot is in progress */
  isBooting: boolean;
  /** Whether boot encountered an error */
  hasError: boolean;
  /** The authenticated user (if any) */
  user: BootState['user'];
  /** Error that occurred during boot (if any) */
  error: Error | null;
  /** Retry boot after failure */
  retry: () => Promise<void>;
  /** Whether running on native platform */
  isNative: boolean;
}

/**
 * Hook to access boot coordinator state
 */
export function useBootState(): UseBootStateReturn {
  const [bootState, setBootState] = useState<BootState>(bootCoordinator.getState());

  useEffect(() => {
    // Subscribe to boot state changes
    const unsubscribe = bootCoordinator.subscribe(setBootState);
    return unsubscribe;
  }, []);

  const retry = useCallback(async () => {
    await bootCoordinator.retry();
  }, []);

  return {
    bootState,
    phase: bootState.phase,
    isReady: bootState.phase === 'ready',
    isBooting: bootState.phase === 'splash' || bootState.phase === 'boot-screen',
    hasError: bootState.phase === 'error',
    user: bootState.user,
    error: bootState.error,
    retry,
    isNative: bootCoordinator.isPlatformNative(),
  };
}

/**
 * Hook to trigger boot sequence
 * Should be used once at app entry point
 */
export function useBootTrigger(): {
  boot: () => Promise<void>;
  isNative: boolean;
} {
  const boot = useCallback(async () => {
    await bootCoordinator.boot();
  }, []);

  return {
    boot,
    isNative: bootCoordinator.isPlatformNative(),
  };
}

export default useBootState;
